import { AnalyticsChartCard } from '@/components/ui/analytics/AnalyticsChartCard';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import type { ContactsAnalyticsData } from '@/types/analytics';
import { Mail, Phone, Star, Users } from 'lucide-react';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

interface ContactsAnalyticsProps {
    contactsAnalytics?: ContactsAnalyticsData;
}

export default function ContactsAnalytics({ contactsAnalytics }: ContactsAnalyticsProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const categoryAxisWidth = isMobile ? 72 : 120;
    const truncateCategoryTick = (v: string) => (isMobile && v.length > 10 ? `${v.slice(0, 9)}…` : v);
    const chartHeightClass = useChartHeightClass('md');
    const totals = contactsAnalytics?.totals ?? {};
    const primaryCoverage = contactsAnalytics?.primary_contact_coverage ?? {};

    const totalContacts = Number(totals.contacts ?? 0);
    const withEmail = Number(totals.with_email ?? 0);
    const withPhone = Number(totals.with_phone ?? 0);
    const withPosition = Number(totals.with_position ?? 0);

    const completenessData = useMemo(() => {
        if (totalContacts === 0) {
            return [];
        }

        return [
            { metric: t('analytics.labels.email', 'Email'), percent: (withEmail / totalContacts) * 100 },
            { metric: t('analytics.labels.phone', 'Phone'), percent: (withPhone / totalContacts) * 100 },
            { metric: t('analytics.labels.position', 'Position'), percent: (withPosition / totalContacts) * 100 },
        ];
    }, [totalContacts, withEmail, withPhone, withPosition, t]);

    const priorityData = (contactsAnalytics?.contacts_by_company_priority ?? []).map((row: { priority: string; count: number }) => ({
        ...row,
        label: settingLabel(t, 'company_priorities', row.priority),
    }));
    const roleData = contactsAnalytics?.position_distribution ?? [];
    const engagementData = contactsAnalytics?.engagement_by_contact ?? [];

    const companiesTotal = Number(primaryCoverage.companies_total ?? 0);
    const companiesWithPrimary = Number(primaryCoverage.companies_with_primary ?? 0);
    const primaryCoveragePct = companiesTotal > 0 ? (companiesWithPrimary / companiesTotal) * 100 : 0;

    const percentChartConfig = {
        percent: {
            label: t('analytics.labels.coverage_pct', 'Coverage %'),
            color: '#3b82f6',
        },
    } satisfies Record<string, { label: string; color: string }>;

    const countChartConfig = {
        count: {
            label: t('analytics.labels.count', 'Count'),
            color: '#8b5cf6',
        },
    } satisfies Record<string, { label: string; color: string }>;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <SummaryMetricCard
                    docKey="contacts.total_contacts"
                    title={t('analytics.labels.total_contacts', 'Total Contacts')}
                    value={totalContacts}
                    subtitle={t('analytics.labels.people_records_in_crm', 'People records in CRM')}
                    icon={<Users className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="contacts.email_completeness"
                    title={t('analytics.labels.email_completeness', 'Email Completeness')}
                    value={`${totalContacts > 0 ? ((withEmail / totalContacts) * 100).toFixed(1) : '0.0'}%`}
                    subtitle={`${withEmail}/${totalContacts} contacts with email`}
                    icon={<Mail className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="contacts.phone_completeness"
                    title={t('analytics.labels.phone_completeness', 'Phone Completeness')}
                    value={`${totalContacts > 0 ? ((withPhone / totalContacts) * 100).toFixed(1) : '0.0'}%`}
                    subtitle={`${withPhone}/${totalContacts} contacts with phone`}
                    icon={<Phone className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="contacts.primary_coverage"
                    title={t('analytics.labels.primary_coverage', 'Primary Coverage')}
                    value={`${primaryCoveragePct.toFixed(1)}%`}
                    subtitle={`${companiesWithPrimary}/${companiesTotal} companies with primary contact`}
                    icon={<Star className="h-4 w-4" />}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <AnalyticsChartCard
                    docKey="contacts.contact_completeness"
                    title={t('analytics.labels.contact_completeness', 'Contact Completeness')}
                    description={t('analytics.labels.share_contacts_core_fields', 'Share of contacts that have core fields filled in.')}
                >
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.contact_completeness', 'Contact Completeness')}
                        config={percentChartConfig}
                        className={chartHeightClass}
                    >
                        <BarChart data={completenessData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="metric" className="text-xs" />
                            <YAxis className="text-xs" tickFormatter={(value) => `${value}%`} />
                            <ChartTooltip
                                content={
                                    <ChartTooltipContent
                                        formatter={(value) => [`${Number(value).toFixed(1)}%`, t('analytics.labels.coverage', 'Coverage')]}
                                    />
                                }
                            />
                            <Bar dataKey="percent" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ChartContainer>
                </AnalyticsChartCard>

                <AnalyticsChartCard
                    docKey="contacts.contacts_by_company_priority"
                    title={t('analytics.labels.contacts_by_company_priority', 'Contacts by Company Priority')}
                    description={t('analytics.labels.contact_volume_by_priority', 'How contact volume is distributed by account priority.')}
                >
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.contacts_by_company_priority', 'Contacts by Company Priority')}
                        config={countChartConfig}
                        className={chartHeightClass}
                    >
                        <BarChart data={priorityData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="label" className="text-xs" />
                            <YAxis className="text-xs" />
                            <ChartTooltip
                                content={<ChartTooltipContent formatter={(value) => [value, t('analytics.labels.contacts', 'Contacts')]} />}
                            />
                            <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ChartContainer>
                </AnalyticsChartCard>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <AnalyticsChartCard
                    docKey="contacts.top_roles_positions"
                    title={t('analytics.labels.top_roles_positions', 'Top Roles / Positions')}
                    description={t('analytics.labels.most_frequent_contact_roles', 'Most frequent contact roles.')}
                >
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.top_roles_positions', 'Top Roles / Positions')}
                        config={countChartConfig}
                        className={chartHeightClass}
                    >
                        <BarChart data={roleData} layout="vertical" margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" horizontal />
                            <XAxis type="number" className="text-xs" />
                            <YAxis
                                dataKey="position"
                                type="category"
                                width={categoryAxisWidth}
                                tickFormatter={truncateCategoryTick}
                                className="text-xs"
                            />
                            <ChartTooltip
                                content={<ChartTooltipContent formatter={(value) => [value, t('analytics.labels.contacts', 'Contacts')]} />}
                            />
                            <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ChartContainer>
                </AnalyticsChartCard>

                <AnalyticsChartCard
                    docKey="contacts.engagement_by_contact"
                    title={t('analytics.labels.engagement_by_contact', 'Engagement by Contact')}
                    description={t('analytics.labels.top_contacts_by_linked_leads_projects', 'Top contacts ranked by linked leads and projects.')}
                >
                    {engagementData.length === 0 ? (
                        <p className="text-muted-foreground py-8 text-center text-sm">
                            {t('analytics.labels.no_engagement_data', 'No contacts with linked activity yet.')}
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {engagementData.slice(0, 10).map((row) => {
                                const name =
                                    [row.first_name, row.last_name].filter(Boolean).join(' ') ||
                                    t('analytics.labels.unnamed_contact', 'Unnamed Contact');
                                return (
                                    <div key={row.person_id} className="flex items-center justify-between rounded-lg border p-3">
                                        <div className="font-medium">{name}</div>
                                        <div className="flex items-center gap-2 text-sm">
                                            {Number(row.leads_count) > 0 && (
                                                <span className="bg-cat-purple text-cat-purple-foreground rounded px-2 py-0.5 text-xs font-medium">
                                                    {row.leads_count} {t('analytics.labels.leads_label', 'leads')}
                                                </span>
                                            )}
                                            {Number(row.projects_count) > 0 && (
                                                <span className="bg-cat-teal text-cat-teal-foreground rounded px-2 py-0.5 text-xs font-medium">
                                                    {row.projects_count} {t('analytics.labels.projects_label', 'projects')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </AnalyticsChartCard>
            </div>
        </div>
    );
}
